import { questionnaireFieldTypeConstante } from "@/constantes/questionnaire-field-type";
import { questionnaireStore } from "@/main";
import { i18n } from "@/i18n";
import { missionViewModelManager } from "@/services/contract-mission/mission-view-model-manager";
import { activityViewModelManager } from "@/services/contract-activity/activity-view-model-manager";
import { moduleManager } from "@/services/module/module-manager";
import { moduleConstantes } from "@/constantes/module";

class QuestionnaireValidator {
  // formValidator.validForm() ignore les champs Mission : isVisible() y cherche un élément DOM
  // dont l'id correspond à model.vars.id (l'id du field), mais le bloc Mission ne rend aucun
  // input à cet id (un textarea par mission, avec des ids différents) — ignoreValidation
  // finit donc toujours à true et handleRequiredErrors() n'est jamais appelé pour ce champ.
  // On valide donc les missions manquantes séparément, ici.
  validateMissionFields() {
    // Le bloc Mission n'est pas rendu quand le module est inactif
    // (QuestionnaireQuestionReplyComponent : v-if="!isMission || missionModuleActive") : valider un
    // champ invisible rendrait le questionnaire impossible à signer.
    if (!moduleManager.isModuleActive(moduleConstantes.MODULE_STUDEA_MISSIONS_ACTIVITIES)) {
      return [];
    }

    const errors = [];
    Object.values(questionnaireStore.questionnaire?.questions || {}).forEach((question) => {
      if (question.type !== questionnaireFieldTypeConstante.QUESTION_TYPE_MISSION) {
        return;
      }

      const model = questionnaireStore.model[question.id];
      if (!model) {
        return;
      }

      if (!question.required) {
        delete model.vars.error;
        return;
      }

      // Compteur incrémenté à chaque tentative de signature (même si l'erreur est identique à
      // la précédente) : le composant Mission s'appuie dessus (et non sur model.vars.error, qui
      // ne change pas de valeur d'une tentative à l'autre) pour re-déclencher l'affichage des
      // erreurs et revenir sur le filtre "En cours" à chaque clic sur "Signer".
      model.vars.validationAttempt = (model.vars.validationAttempt || 0) + 1;

      const edits = model.vars.missionsEvolutions || {};
      let emptyObjectifId;
      const hasMissingObjectif = (question.missionEvolutions || []).some((evolution) => {
        emptyObjectifId = missionViewModelManager.getMissionObjectifFieldId(
          missionViewModelManager.toMissionViewModel(evolution),
          edits[evolution.contractMission?.id],
        );
        return emptyObjectifId;
      });

      if (hasMissingObjectif) {
        model.vars.error = i18n.global.t("requiredField");
        errors.push(emptyObjectifId);
      } else if (model.vars.error) {
        delete model.vars.error;
      }
    });
    return errors;
  }

  // Miroir de validateMissionFields() ci-dessus, même raison d'être : formValidator.validForm()
  // ignore le bloc Activité (rendu Vue dédié, aucun input dont l'id correspond à model.vars.id).
  // Contrairement à Mission, le bloc Activité n'est pas masqué par
  // QuestionnaireQuestionReplyComponent quand MODULE_STUDEA_MISSIONS_ACTIVITIES est inactif
  // (v-if="!isMission || missionModuleActive" ne conditionne que isMission) : pas de garde module
  // ici, elle bloquerait à tort une signature sur un bloc pourtant bien affiché.
  validateActivityFields() {
    const errors = [];
    Object.values(questionnaireStore.questionnaire?.questions || {}).forEach((question) => {
      if (question.type !== questionnaireFieldTypeConstante.QUESTION_TYPE_ACTIVITY) {
        return;
      }

      const model = questionnaireStore.model[question.id];
      if (!model) {
        return;
      }

      if (!question.required) {
        delete model.vars.error;
        return;
      }

      // cf. validateMissionFields() : compteur incrémenté à chaque tentative de signature, pour
      // que QuestionnaireActivityComponent puisse re-déclencher l'affichage des erreurs (via son
      // watch sur model.vars.validationAttempt) même quand l'erreur reste identique d'un essai à
      // l'autre.
      model.vars.validationAttempt = (model.vars.validationAttempt || 0) + 1;

      const edits = model.vars.activitiesEvolutions || {};
      let missingCommentaireFieldId;
      const hasMissingCommentaire = (question.activityEvolutions || []).some((evolution) => {
        const activity = activityViewModelManager.toActivityViewModel(evolution);
        if (!activityViewModelManager.isActivityCommentaireMissing(activity, edits[activity.id], true)) {
          return false;
        }
        missingCommentaireFieldId = "activity-preview-comment-" + activity.id;
        return true;
      });

      if (hasMissingCommentaire) {
        model.vars.error = i18n.global.t("requiredField");
        errors.push(missingCommentaireFieldId);
      } else if (model.vars.error) {
        delete model.vars.error;
      }
    });
    return errors;
  }

  validQuestion(question, valid) {
    if (question.type === null && question.itemType !== "cluster") valid = false;
    const isCluster = question.itemType === "cluster" && question.questions && Object.keys(question.questions).length;
    if (question.itemType === "field") {
      if (question.access.length === 0) {
        question["accessError"] = true;
      } else {
        delete question.accessError;
      }

      if (!question.title) {
        question["titleError"] = true;
      } else {
        delete question.titleError;
      }

      if (valid && (question.accessError || question.titleError)) {
        valid = false;
      }
    } else if (isCluster) {
      if (!question.title) {
        question["titleError"] = true;
      } else {
        delete question.titleError;
      }
      Object.values(question.questions).forEach((row) => {
        if (row.access.length === 0) {
          row["accessError"] = true;
          row.selected = question.selected;
        } else {
          delete row.accessError;
        }

        if (!row.title) {
          row["titleError"] = true;
        } else {
          delete row.titleError;
        }

        if (valid && (row.accessError || row.titleError)) {
          valid = false;
        }
        if (row.required && !question.valueList.length) {
          valid = false;
        }
      });
      Object.values(question.valueList).forEach((value) => {
        if (!value.value.length) {
          value["error"] = i18n.global.t("studea_manager.questionnaire.value_title_missing");
        } else {
          delete value.error;
        }

        if (valid && value.error) {
          valid = false;
        }
      });
    }
    return valid;
  }
}

export const questionnaireValidator = new QuestionnaireValidator();
